import { useState, useEffect } from "react";
import { supabase } from "../supabase/supabaseClient";
import { useNavigate } from "react-router-dom";

export default function SignupNickname() {
  const [nickname, setNickname] = useState("");
  const [userType, setUserType] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const forbiddenWords = [
  "관리자", "운영자", "admin", "system", "moderator", "sysop", "운영팀", "master", "staff",
  "카카오", "구글", "삼성", "네이버", "청와대", "경찰", "국회", "대통령", "헌법재판소", "검찰",
  "대표", "교수", "선생", "의사", "변호사", "판사", "의원", "기자", "장관",
  "fuck", "shit", "bitch", "asshole", "시발", "좆", "병신", "씨발", "개새끼", "ㅅㅂ", "ㅂㅅ", "ㄱㅅ",
  "멍청이", "정신병", "장애인", "미친놈", "쓰레기", "개새", "개새끼", "개자식", "개새꺄", "개새끼야",
  "개새끼야", "개새끼야", "개새끼야", "관리인",
  "알림", "시스템", "공지", "익명", "unknown", "anonymous", "비공개", "ABlind", "A-Blind", "에이블라인드", 
  "A_Blind", "ABLIND", "A_BLIND", "AB", "a blind", "A-BLIND", "Ablind", "A Blind", "에이 블라인드",
];

function isNicknameForbidden(nickname: string) {
  const lowerNickname = nickname.toLowerCase();
  for (const word of forbiddenWords) {
    if (lowerNickname.includes(word.toLowerCase())) {
      return true;
    }
  }
  // 개인정보 패턴(폰/이메일 등)
  const phoneOrEmailRegex = /\d{3}[- ]?\d{3,4}[- ]?\d{4}|@[a-z]/i;
  if (phoneOrEmailRegex.test(nickname)) return true;
  // 너무 짧거나 특수문자만
  if (/^[^a-zA-Z0-9가-힣]+$/.test(nickname) || nickname.replace(/[\s_]/g, "").length < 2) return true;
  return false;
}
  useEffect(() => {
    async function getUserType() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // user_metadata에 정보가 있으면 기본값으로 세팅
        if (user.user_metadata?.userType) setUserType(user.user_metadata.userType);
      }
    }
    getUserType();
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!nickname.trim() || !userType) {
      setError("닉네임과 가입 유형을 모두 선택하세요.");
      return;
    }

    // 닉네임이 금지된 단어인지 확인하는 함수
    if (isNicknameForbidden(nickname.trim())) {
    setError("사용할 수 없는 닉네임입니다.\n(운영자/시스템/욕설/브랜드/개인정보 등 금지)");
    return;
  }

    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError("로그인이 필요합니다.");
      return;
    }

    // 닉네임 중복 체크 (자기 자신 제외)
    const { data: dup, error: checkError } = await supabase
      .from("profiles")
      .select("id")
      .eq("nickname", nickname.trim())
      .neq("id", user.id);

    if (checkError) {
      setError("닉네임 확인 중 오류: " + checkError.message);
      return;
    }
    if (dup && dup.length > 0) {
      setError("이미 사용 중인 닉네임입니다.");
      return;
    }

    // profiles row 존재 여부 확인
    const { data: existingProfile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    // userType 저장(없으면 새로 insert, 있으면 update)
    const profileData = {
      nickname: nickname.trim(),
      user_type: userType,
      level: "pending",
      email: user.email,
    };

    if (!existingProfile) {
      // insert
      const { error: insertError } = await supabase.from("profiles").insert({
        id: user.id,
        ...profileData,
      });
      if (insertError) {
        if (
          insertError.message.includes("duplicate key value") ||
          insertError.message.includes("unique constraint")
        ) {
          setError("이미 사용 중인 닉네임입니다.");
        } else {
          setError("저장 실패: " + insertError.message);
        }
        return;
      }
    } else {
      // update
      const { error: updateError } = await supabase
        .from("profiles")
        .update(profileData)
        .eq("id", user.id);
      if (updateError) {
        if (
          updateError.message.includes("duplicate key value") ||
          updateError.message.includes("unique constraint")
        ) {
          setError("이미 사용 중인 닉네임입니다.");
        } else {
          setError("저장 실패: " + updateError.message);
        }
        return;
      }
    }

    setSuccess(true);
    setTimeout(() => window.location.href = "/", 1300);
  }

  return (
    <form
      className="max-w-md mx-auto mt-20 p-8 shadow rounded bg-white"
      onSubmit={handleSave}
    >
      <h2 className="text-2xl font-bold mb-6 text-center">추가 정보 입력</h2>

      <input
        type="text"
        className="border p-2 mb-2 w-full"
        placeholder="닉네임"
        value={nickname}
        onChange={e => setNickname(e.target.value)}
        autoFocus
      />

      {/* 가입유형 버튼 방식 */}
      <div className="mb-4">
        <label className="block mb-1 text-sm font-semibold">가입 유형 선택</label>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            className={`px-4 py-2 rounded border transition
              ${userType === "artist"
                ? "bg-black text-white border-black"
                : "bg-gray-100 border-gray-400 text-gray-700"}`}
            onClick={() => setUserType("artist")}
          >
            예술가
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded border transition
              ${userType === "worker"
                ? "bg-black text-white border-black"
                : "bg-gray-100 border-gray-400 text-gray-700"}`}
            onClick={() => setUserType("worker")}
          >
            예술 종사자
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded border transition
              ${userType === "fan"
                ? "bg-black text-white border-black"
                : "bg-gray-100 border-gray-400 text-gray-700"}`}
            onClick={() => setUserType("fan")}
          >
            예술 애호가
          </button>
        </div>
      </div>

      {error && <div className="mb-2 text-red-500 text-center">{error}</div>}
      {success ? (
        <div className="mb-2 text-green-600 font-semibold text-center">
          저장되었습니다! 메인으로 이동합니다.
        </div>
      ) : (
        <button
          type="submit"
          className="w-full bg-black text-white py-2 rounded"
        >
          저장
        </button>
      )}
    </form>
  );
}
